import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { api, IDR, imgUrl } from "@/lib/api";
import { toast } from "sonner";

const F = "block text-[10px] tracking-widest text-neutral-500 mb-1";
const I = "w-full bg-transparent border border-[color:var(--border)] px-3 py-3 text-sm focus:border-white outline-none";
const PLACE = "https://images.unsplash.com/photo-1503341504253-dff4815485f1?crop=entropy&cs=srgb&fm=jpg";

function loadSnap(clientKey) {
  return new Promise((resolve, reject) => {
    if (window.snap) return resolve(window.snap);
    const s = document.createElement("script");
    s.src = "https://app.sandbox.midtrans.com/snap/snap.js";
    s.setAttribute("data-client-key", clientKey || "");
    s.onload = () => resolve(window.snap);
    s.onerror = () => reject(new Error("Snap.js failed to load"));
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    address: "", province: "", city: "", district: "", postal_code: "", shipping_notes: "",
  });
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [validating, setValidating] = useState(false);
  const [origin, setOrigin] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const timer = useRef(null);

  const totalQty = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);

  useEffect(() => {
    api.get("/settings").then((r) => setOrigin(r.data?.shipping_origin || null));
  }, []);

  useEffect(() => {
    if (!form.province || !form.city || totalQty < 1) { setQuote(null); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setQuoting(true);
      try {
        const { data } = await api.post("/shipping/quote", {
          province: form.province, city: form.city, qty: totalQty,
        });
        setQuote(data);
      } catch { setQuote(null); }
      finally { setQuoting(false); }
    }, 400);
    return () => clearTimeout(timer.current);
  }, [form.province, form.city, totalQty]);

  if (items.length === 0 && !busy) {
    return <div className="max-w-xl mx-auto p-16 text-center text-neutral-400">Keranjang kosong. <button onClick={() => nav("/shop")} className="underline">Kembali belanja</button></div>;
  }
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const applyCode = async () => {
    if (!code.trim() || !form.customer_email) return toast.error("Isi email dulu untuk validasi kode");
    setValidating(true);
    try {
      const { data } = await api.post("/discounts/validate", { code, email: form.customer_email, subtotal });
      setDiscount(data.discount_amount);
      toast.success(`Kode ${data.code} aktif — potongan ${IDR(data.discount_amount)}`);
    } catch (err) {
      setDiscount(0);
      toast.error(err?.response?.data?.detail || "Kode tidak valid");
    } finally { setValidating(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    for (const k of ["customer_name","customer_email","customer_phone","address","province","city","district","postal_code"]) {
      if (!form[k]) return toast.error("Lengkapi seluruh data pengiriman");
    }
    setBusy(true);
    try {
      const payload = { ...form,
        items: items.map((i) => ({ product_id: i.product_id, size: i.size, color: i.color, qty: i.qty })),
        discount_code: code.trim() || null,
      };
      const { data } = await api.post("/checkout", payload);
      if (data.payment?.mock) {
        await api.post(`/payments/mock-confirm/${data.order_number}`);
        clear();
        nav(`/order/${data.order_number}?email=${encodeURIComponent(form.customer_email)}`);
        return;
      }
      if (data.payment?.snap_token) {
        const snap = await loadSnap(data.payment.client_key);
        snap.pay(data.payment.snap_token, {
          onSuccess: () => { clear(); nav(`/order/${data.order_number}?email=${encodeURIComponent(form.customer_email)}`); },
          onPending: () => { clear(); nav(`/order/${data.order_number}?email=${encodeURIComponent(form.customer_email)}`); },
          onError: () => toast.error("Pembayaran gagal. Coba lagi."),
          onClose: () => toast.message("Jendela pembayaran ditutup. Order menunggu pembayaran."),
        });
      } else {
        toast.error(data.payment?.error || "Gagal membuat pembayaran");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout gagal");
    } finally { setBusy(false); }
  };

  const shipping = quote?.cost || 0;
  const totalEst = Math.max(0, subtotal - discount) + shipping;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
      <h1 className="display text-5xl md:text-7xl mb-8">CHECKOUT</h1>
      <div className="grid lg:grid-cols-[1fr,420px] gap-10">
        <form onSubmit={submit} className="space-y-5" data-testid="checkout-form">
          {origin && (
            <div data-testid="origin-banner" className="border border-[color:var(--border)] px-4 py-3 text-[11px] tracking-widest text-neutral-400">
              DIKIRIM DARI <span className="text-white">{origin.city.toUpperCase()}, {origin.province.toUpperCase()}</span> · SETIAP ORDER FERAL RIOT DIKEMAS &amp; DIKIRIM DARI PRODUCTION HOUSE KAMI DI MAJALENGKA.
            </div>
          )}

          <div className="text-[10px] tracking-widest text-neutral-500">DATA PELANGGAN</div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className={F}>Nama Lengkap</label><input data-testid="in-name" className={I} value={form.customer_name} onChange={upd("customer_name")} /></div>
            <div><label className={F}>Email</label><input data-testid="in-email" type="email" className={I} value={form.customer_email} onChange={upd("customer_email")} /></div>
            <div className="md:col-span-2"><label className={F}>WhatsApp / Telepon</label><input data-testid="in-phone" className={I} value={form.customer_phone} onChange={upd("customer_phone")} /></div>
          </div>
          <div className="text-[10px] tracking-widest text-neutral-500 pt-4">ALAMAT PENGIRIMAN</div>
          <div><label className={F}>Alamat Lengkap</label><textarea data-testid="in-address" rows={3} className={I} value={form.address} onChange={upd("address")} /></div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className={F}>Provinsi</label><input data-testid="in-province" className={I} value={form.province} onChange={upd("province")} placeholder="Jawa Barat" /></div>
            <div><label className={F}>Kota / Kabupaten</label><input data-testid="in-city" className={I} value={form.city} onChange={upd("city")} placeholder="Bandung" /></div>
            <div><label className={F}>Kecamatan</label><input data-testid="in-district" className={I} value={form.district} onChange={upd("district")} /></div>
            <div><label className={F}>Kode Pos</label><input data-testid="in-postal" className={I} value={form.postal_code} onChange={upd("postal_code")} /></div>
          </div>
          <div><label className={F}>Catatan Pengiriman (opsional)</label><textarea data-testid="in-notes" rows={2} className={I} value={form.shipping_notes} onChange={upd("shipping_notes")} /></div>

          <div className="text-[10px] tracking-widest text-neutral-500 pt-4">KODE DISKON</div>
          <div className="flex gap-2">
            <input data-testid="in-code" placeholder="Punya kode? Masukkan di sini" className={I} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
            <button data-testid="apply-code" type="button" onClick={applyCode} disabled={validating} className="border border-white px-6 text-xs tracking-widest disabled:opacity-60">TERAPKAN</button>
          </div>

          <button data-testid="place-order-btn" disabled={busy} className="w-full bg-white text-black py-4 text-xs tracking-widest hover:bg-neutral-200 disabled:opacity-60">
            {busy ? "MEMPROSES..." : "BAYAR SEKARANG"}
          </button>
          <p className="text-[10px] text-neutral-500">*Pembayaran diproses melalui Midtrans (QRIS, GoPay, OVO, VA, kartu). Saat mode Sandbox/mock aktif, order langsung terverifikasi untuk pengujian.</p>
        </form>

        <aside className="border border-[color:var(--border)] p-6 h-fit sticky top-24">
          <div className="text-[10px] tracking-widest text-neutral-500 mb-4">PESANAN KAMU</div>
          <div className="space-y-4 max-h-72 overflow-auto">
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-[60px,1fr,auto] gap-3 items-center">
                <img src={imgUrl(it.image) || PLACE} className="w-14 h-16 object-cover" alt="" />
                <div className="min-w-0">
                  <div className="text-[10px] text-neutral-500">{it.article_code}</div>
                  <div className="text-sm truncate">{it.name}</div>
                  <div className="text-[10px] text-neutral-500">{it.color} · {it.size} · x{it.qty}</div>
                </div>
                <div className="text-sm tabular-nums">{IDR(it.price * it.qty)}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-[color:var(--border)] my-4" />
          <div className="flex justify-between text-sm py-1"><span>Subtotal</span><span className="tabular-nums">{IDR(subtotal)}</span></div>
          {discount > 0 && (
            <div data-testid="discount-line" className="flex justify-between text-sm py-1 text-emerald-400"><span>Diskon</span><span className="tabular-nums">− {IDR(discount)}</span></div>
          )}
          <div data-testid="shipping-line" className="flex justify-between text-sm py-1">
            <span>Ongkir {origin ? `(${origin.city})` : ""}</span>
            <span className="tabular-nums">
              {quoting ? "…" : quote ? IDR(quote.cost) : <span className="text-neutral-500">Isi provinsi &amp; kota</span>}
            </span>
          </div>
          {quote && (
            <div data-testid="eta-line" className="flex justify-between text-[11px] py-1 text-neutral-400">
              <span>Estimasi tiba</span>
              <span>{quote.eta_min_days}–{quote.eta_max_days} hari kerja</span>
            </div>
          )}
          {quote && (
            <div className="text-[10px] text-neutral-500 mt-1">Zona: {quote.zone_label}</div>
          )}
          <div className="flex justify-between display text-2xl mt-3"><span>TOTAL</span><span data-testid="total-est" className="tabular-nums">{IDR(totalEst)}</span></div>
        </aside>
      </div>
    </div>
  );
}
